import { IconPhoto } from '@tabler/icons-react';
import { IconMapper } from '../../../common/helpers/IconMapper';
import CdnImage from '../../../components/CdnImage';
import BoatFacilitiesSkeleton from './BoatFacilitiesSkeleton';

interface BoatFacilities {
  name: string;
  iconName?: string | null;
}

interface BoatPhotos {
  boatId: number;
  imageUrl: string;
  isHero?: boolean | null;
}

interface BoatInfoComponentProps {
  boatName: string;
  rating: number;
  heroImageUrl: string;
  imageUrls: BoatPhotos[];
  boatFacilities: BoatFacilities[];
  boatFacilitiesLoading: boolean;
  setShowBoatPhotos: (show: boolean) => void;
}

const BoatInfoComponent = (props: BoatInfoComponentProps) => {
  return (
    <div className="pt-6">
      {/* Boat Name */}
      <h1 className="text-xl font-semibold text-neutral-800 md:text-2xl">
        {props.boatName}
      </h1>

      {/* Boat Facilities */}
      <div className="pt-4 flex items-center content-center gap-2 self-stretch">
        <div className="flex flex-wrap items-center gap-2">
          {!props.boatFacilitiesLoading ? (
            props.boatFacilities.map((facility, index) => (
              <div
                key={index}
                className="flex py-1 px-3 items-center gap-2 rounded-full bg-neutral-50"
              >
                {IconMapper(facility.iconName ?? '', 16)}
                <span className="text-sm font-normal leading-normal text-neutral-800">
                  {facility.name}
                </span>
              </div>
            ))
          ) : (
            <BoatFacilitiesSkeleton />
          )}
        </div>
      </div>

      {/* Mobile Hero Image */}
      <div className="mt-8 flex flex-col items-start gap-2 flex-shrink-0 relative md:hidden">
        <img
          className="w-full h-auto rounded-2xl"
          src={props.heroImageUrl}
          alt={props.boatName}
          style={{ objectFit: 'cover' }}
        />
        <button
          className="inline-flex p-2 px-4 justify-center items-center gap-2 rounded-md border border-neutral-800 bg-white shadow-m absolute bottom-4 right-4"
          onClick={() => {
            props.setShowBoatPhotos(true);
          }}
        >
          <IconPhoto size={16} />
          <span className="text-sm font-semibold ">See all photos</span>
        </button>
      </div>

      {/* Desktop Images */}
      <div className="gap-2 w-full mt-12 hidden md:grid grid-cols-2">
        <div className="w-full">
          {props.imageUrls.map(
            (image, index) =>
              image.isHero && (
                <div key={index} className="relative w-full h-0 pb-[66.66%]">
                  <CdnImage
                    className="absolute top-0 left-0 w-full h-full object-cover rounded-l-3xl"
                    src={image.imageUrl}
                    variant="BOATPAGE"
                    alt={props.boatName}
                  />
                </div>
              ),
          )}
        </div>
        <div className="grid grid-cols-2 gap-2 relative">
          {props.imageUrls
            .filter(x => !x.isHero)
            .slice(0, 4)
            .map((image, index) => (
              <div
                key={index}
                className={`relative w-full pb-[50%] ${
                  index === 1 ? 'rounded-tr-3xl' : ''
                } ${index === 3 ? 'rounded-br-3xl' : ''}`}
              >
                <CdnImage
                  className="absolute top-0 left-0 w-full h-full object-cover"
                  src={image.imageUrl}
                  variant="BOATPAGE"
                  alt={props.boatName}
                />
              </div>
            ))}
          <button
            className="inline-flex p-2 px-4 justify-center items-center gap-2 rounded-md border border-neutral-800 bg-white shadow-m absolute bottom-6 right-6"
            onClick={() => {
              props.setShowBoatPhotos(true);
            }}
          >
            <IconPhoto size={16} />
            <span className="text-sm font-semibold ">See all photos</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default BoatInfoComponent;
